import React from 'react';
import BEM from '../../bem';
import mobxInjectSelect from '../../utils/mobxInjectSelect';
import { useMessageTask } from '../../hooks/useMessageTask';
import MessageTaskAffordance from './MessageTaskAffordance';
import MessageTaskCard from './MessageTaskCard';

const classes = BEM.with('MessageTaskContainer');

/** Minimal shape of the source message this container reads. */
type TaskMessage = {
  id: string;
  serverId?: string;
  body: string;
  createdAt: string | Date;
  sender?: { displayName?: string };
};

type MessageTaskContainerProps = {
  direction?: 'INCOMING' | 'OUTGOING';
  message: TaskMessage;
  variant?: 'affordance' | 'card';
};

type MobxProps = {
  currentOrganizationId: string;
  currentUserId: string;
  isTaskManagerEnabled: boolean;
  openModal: (type: string, options: Record<string, unknown>) => void;
};

function MessageTaskContainer({
  currentOrganizationId,
  currentUserId,
  direction,
  isTaskManagerEnabled,
  message,
  openModal,
  variant,
}: MessageTaskContainerProps & MobxProps) {
  const messageId = message.serverId || message.id;
  const { task, hasTask, isLoading } = useMessageTask(messageId, currentOrganizationId);

  // Feature-gated on the org's Task Manager setting + LaunchDarkly flag.
  if (!isTaskManagerEnabled) return null;
  if (isLoading) return null;

  if (hasTask && task) {
    if (task.creator_id !== currentUserId) return null;
    if (variant === 'affordance') return null;
    return (
      <div className={classes({ direction, card: true })}>
        <MessageTaskCard organizationId={currentOrganizationId} task={task} />
      </div>
    );
  }

  if (variant === 'card') return null;
  return (
    <div className={classes({ affordance: true, direction })}>
      <MessageTaskAffordance onClick={() => openModal('createTask', { message })} />
    </div>
  );
}

export default mobxInjectSelect<MessageTaskContainerProps, MobxProps>({
  modalStore: ['openModal'],
  messengerStore: ['currentOrganizationId', 'isTaskManagerEnabled'],
  sessionStore: ['currentUserId'],
})(MessageTaskContainer);
